import { useEffect, useState } from "react"
import { CopyRightText } from "@/components/custom/CopyRightText"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { timezones } from '../../constants/auth/timezone'
import { TCSLink } from "@/components/custom/Link/TCSLink"
import { useNavigate } from "react-router-dom"
import { ResponseMessageBox } from "@/components/custom/ResponseMessageBox/ResponseMessageBox"
import { Auth } from "@/API/Auth"
import { Label } from "@/components/ui/label"
import { useCookies } from "react-cookie"
import { useDispatch } from 'react-redux'
import { AppDispatch } from "@/redux"
import { userActions } from "@/redux/slice/userSlice"
import { AuthSchema } from "@/constants/auth/auth"
import { SignupForm } from "@/types/auth"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import {
  Card,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export const SignUp = () => {
  const navigate = useNavigate()
  const dispatch: AppDispatch = useDispatch()
  const [formMessage, setFormMessage] = useState('')
  const [, setCookie] = useCookies(['tcsRefreshToken', 'tcsAccessToken'])

  const form = useForm<SignupForm>({
    resolver: zodResolver(AuthSchema.signup),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      firstName: "",
      lastName: "",
      organization: "",
      timezone: "",
      agreeTerms: false
    },
  })

  useEffect(() => {
    setFormMessage('')
    form.clearErrors()
  }, [form])

  const onSignupSubmit = async (
    values: SignupForm
  ) => {
    if (values.password !== values.confirmPassword) {
      form.setError('password', { message: 'Passwords do not match.' })
      form.setError('confirmPassword', { message: 'Passwords do not match.' })
      return
    }
    const data = {
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      password: values.password,
      organization: values.organization,
      timezone: values.timezone
    }
    const response = await Auth.signup(data)

    if (!response.data && response.err) {
      setFormMessage(response.err)
      return
    }
    if (response.data) {
      // set token
      setCookie('tcsRefreshToken', response.data.refreshToken.token, {
        domain: document.location.hostname,
        path: '/',
        expires: new Date(response.data.refreshToken.expiresAt)
      })
      setCookie('tcsAccessToken', response.data.accessToken.token, {
        domain: document.location.hostname,
        path: '/',
        expires: new Date(response.data.accessToken.expiresAt)
      })
      dispatch(userActions.setUserToken(response.data.accessToken.token))
      setFormMessage('')
      form.reset({
        email: "",
        password: "",
        confirmPassword: "",
        firstName: "",
        lastName: "",
        organization: "",
        timezone: ""
      })
      navigate('/router')
    }
  }

  return <div className="authPage bg-opacity-50 py-20 flex flex-col justify-center items-center">
    <Card className="auth-card w-sm flex-column p-10">
      <CardHeader className="mb-12">
        <CardTitle>The Camp Stack</CardTitle>
      </CardHeader>

      {formMessage && <ResponseMessageBox messageTitle={formMessage} />}

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSignupSubmit)} className="space-y-8">
          <Label className="flex justify-start font-semibold text-secondary mb-10">
            ACCOUNT DETAILS
          </Label>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className="flex justify-start mb-2 font-semibold text-primary"
                  isRequired
                >
                  Email Address
                </FormLabel>
                <FormControl>
                  <Input
                    hasError={!!form.formState.errors.email}
                    type="email"
                    placeholder="john@example.com"
                    {...field}
                  />
                </FormControl>
                <FormMessage
                  showIcon={true}
                  className="flex justify-start"
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className="flex justify-start font-semibold text-primary"
                  isRequired
                >
                  Password
                </FormLabel>
                <FormControl>
                  <Input
                    hasError={!!form.formState.errors.password}
                    type="password"
                    placeholder="********"
                    {...field}
                  />
                </FormControl>
                <FormMessage
                  showIcon={true}
                  className="flex justify-start"
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className="flex justify-start font-semibold text-primary"
                  isRequired
                >
                  Confirm Password
                </FormLabel>
                <FormControl>
                  <Input
                    hasError={!!form.formState.errors.confirmPassword}
                    type="password"
                    placeholder="********"
                    {...field}
                  />
                </FormControl>
                <FormMessage
                  showIcon={true}
                  className="flex justify-start"
                />
              </FormItem>
            )}
          />
          <Label className="flex justify-start font-semibold text-secondary pt-12 pb-4">
            PERSONAL DETAILS
          </Label>
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className="flex justify-start font-semibold text-primary"
                  isRequired
                >
                  First Name
                </FormLabel>
                <FormControl>
                  <Input
                    hasError={!!form.formState.errors.firstName}
                    type="text"
                    placeholder="John"
                    {...field}
                  />
                </FormControl>
                <FormMessage
                  showIcon={true}
                  className="flex justify-start"
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel
                  className="flex justify-start font-semibold text-primary"
                  isRequired
                >
                  Last Name
                </FormLabel>
                <FormControl>
                  <Input
                    hasError={!!form.formState.errors.lastName}
                    type="text"
                    placeholder="Doe"
                    {...field}
                  />
                </FormControl>
                <FormMessage
                  showIcon={true}
                  className="flex justify-start"
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="organization"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex justify-start font-semibold text-primary">
                  Organization
                </FormLabel>
                <FormControl>
                  <Input
                    type="text"
                    placeholder="XYZ"
                    {...field}
                  />
                </FormControl>
                <FormMessage
                  showIcon={true}
                  className="flex justify-start"
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="timezone"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex justify-start font-semibold text-primary">
                  Timezone
                </FormLabel>
                <FormControl>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger aria-label="Select timezone">
                      <SelectValue placeholder="Select Timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {timezones.length && timezones.map((t) => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage
                  showIcon={true}
                  className="flex justify-start"
                />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="agreeTerms"
            render={({ field }) => (
              <FormItem>
                <div className="flex flex-row items-center justify-start">
                  <FormControl>
                    <Checkbox
                      id='termsAndConditions'
                      aria-label="I have read and agree to the Terms of Use"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <label htmlFor="termsAndConditions" className="ml-4 cursor-pointer text-sm">
                    I have read and agree to the&nbsp;
                    <a href="https://thecampstack.com/terms" target="_blank" rel="noopener noreferrer" className="text-accent font-bold">
                      Terms of Use
                    </a>.
                  </label>
                </div>
                <FormMessage
                  showIcon={true}
                  className="flex justify-start"
                />
              </FormItem>
            )}
          />
          <Button type="submit" className="w-full font-semibold">
            Sign Up
          </Button>
        </form>
      </Form>

      <CardFooter className="pt-3 flex justify-center">
        <p className="text-sm pr-1 poppins-extralight">Already have an account?</p>
        <TCSLink
          linkTo='/login'
          className="text-sm text-accent underline font-semibold"
          linkText="Log In"
        />
      </CardFooter>
    </Card>
    <CopyRightText />
  </div>
}
